import { BarChartOutlined, DownOutlined, EditOutlined, EyeOutlined, FilePdfOutlined, ReloadOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Drawer, Empty, Input, Row, Select, Space, Spin, Tabs, Tag, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import type { ColumnsType } from "antd/es/table";
import { useQueryClient } from "@tanstack/react-query";
import { getBillEntry } from "../../api/entryApi";
import {
  generatePdfReport,
  getBalanceSheet,
  getLedgerVoucherMonthly,
  getLedgerVoucherSummary,
  getLedgerVoucherTransactions,
  getPdfReportTypes,
  getProfitLoss,
  getTrialBalance,
  type LedgerMonthRow,
  type LedgerTransactionRow,
  type LedgerVoucherRow,
  type PdfReportType
} from "../../api/reportApi";
import { accountingUiSettings } from "../../app/settings/uiSettings";
import { PageHeaderActions } from "../../shared/components/PageHeaderActions";
import { PersistedTable } from "../../shared/components/PersistedTable";
import { hasPermission } from "../../shared/utils/permissions";
import { formatInr } from "../../shared/utils/currency";
import { balanceText } from "../../shared/utils/balanceDisplay";
import { formatDate } from "../../shared/utils/date";
import { useTenantStore } from "../../stores/tenantStore";
import { VoucherEntryDrawer } from "../vouchers/VoucherEntryDrawer";
import {
  MetricCard,
  ReportPreviewDrawer,
  StatementCard,
  trialColumns,
  useStatementTotals,
  type PdfViewerState
} from "./reportWidgets";

export function ReportsPage({ title = "Report Centre" }: { title?: string }) {
  const orgId = useTenantStore((x) => x.orgId);
  const financialYearId = useTenantStore((x) => x.financialYearId);
  const ready = Boolean(orgId && financialYearId);

  if (title === "Ledger Vouchers") return <LedgerVouchersReport ready={ready} />;
  return <ReportCentre ready={ready} orgId={orgId} financialYearId={financialYearId} />;
}

function ReportCentre({ ready, orgId, financialYearId }: { ready: boolean; orgId: string | null; financialYearId: string | null }) {
  const [filters, setFilters] = useState(getDefaultReportFilters);
  const [activeTab, setActiveTab] = useState(accountingUiSettings.showFinancialStatementsInReportCentre ? "pdf" : "pdf");
  const [pdfViewer, setPdfViewer] = useState<PdfViewerState>({ open: false, loading: false, title: "Report Preview" });
  const showFinancialStatements = accountingUiSettings.showFinancialStatementsInReportCentre;

  useEffect(() => () => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
  }, [pdfViewer.url]);

  const pdfTypes = useQuery({
    queryKey: ["pdf-report-types"],
    enabled: ready,
    queryFn: getPdfReportTypes
  });

  const trialBalance = useQuery({
    queryKey: ["trial-balance", orgId, financialYearId, filters.fromDate, filters.toDate],
    enabled: ready && showFinancialStatements,
    queryFn: () => getTrialBalance({ organizationId: orgId!, financialYearId: financialYearId!, from: filters.fromDate, to: filters.toDate })
  });
  const profitLoss = useQuery({
    queryKey: ["profit-loss", orgId, financialYearId, filters.fromDate, filters.toDate],
    enabled: ready && showFinancialStatements,
    queryFn: () => getProfitLoss({ organizationId: orgId!, financialYearId: financialYearId!, from: filters.fromDate, to: filters.toDate })
  });
  const balanceSheet = useQuery({
    queryKey: ["balance-sheet", orgId, financialYearId, filters.toDate],
    enabled: ready && showFinancialStatements,
    queryFn: () => getBalanceSheet({ organizationId: orgId!, financialYearId: financialYearId!, asOnDate: filters.toDate })
  });

  const trialTotals = useMemo(() => {
    return (trialBalance.data ?? []).reduce((acc, row) => ({
      debit: acc.debit + Number(row.debit ?? 0),
      credit: acc.credit + Number(row.credit ?? 0),
      balance: acc.balance + Number(row.balance ?? 0)
    }), { debit: 0, credit: 0, balance: 0 });
  }, [trialBalance.data]);
  const profitTotals = useStatementTotals(profitLoss.data);
  const balanceTotals = useStatementTotals(balanceSheet.data);

  const previewPdf = async (reportType: string, reportTitle: string) => {
    setPdfViewer((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { open: true, loading: true, title: reportTitle };
    });
    try {
      const blob = await generatePdfReport(reportType, buildPdfPayload(filters));
      setPdfViewer({ open: true, loading: false, title: reportTitle, url: URL.createObjectURL(blob) });
    } catch (error) {
      setPdfViewer({ open: true, loading: false, title: reportTitle, error: error instanceof Error ? error.message : "Unable to generate PDF." });
    }
  };

  const closePdfViewer = () => {
    setPdfViewer((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { open: false, loading: false, title: "Report Preview" };
    });
  };

  const pdfCards = (
    <ReportCardGrid
      loading={pdfTypes.isLoading}
      reports={pdfTypes.data ?? []}
      onPreview={previewPdf}
    />
  );

  return (
    <div className="reports-workbench">
      <PageHeaderActions title="Report Centre" subtitle="PDF report centre matching the Accounting.Web report cards." />
      <div className="report-centre-filter-card">
        <div className="report-centre-filter-bar">
          <label>
            <span>From Date</span>
            <input type="date" value={filters.fromDate} onChange={(event) => setFilters((current) => ({ ...current, fromDate: event.target.value }))} />
          </label>
          <label>
            <span>To Date</span>
            <input type="date" value={filters.toDate} onChange={(event) => setFilters((current) => ({ ...current, toDate: event.target.value }))} />
          </label>
          <label>
            <span>Search / Party</span>
            <Input size="small" placeholder="Optional..." value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} />
          </label>
        </div>
      </div>

      {!ready ? (
        <Card><Typography.Text type="secondary">Select company and financial year to load reports.</Typography.Text></Card>
      ) : showFinancialStatements ? (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: "pdf", label: "PDF Reports", children: pdfCards },
            {
              key: "financial",
              label: "Financial Statements",
              children: (
                <FinancialStatementsPanel
                  trialBalance={trialBalance}
                  profitLoss={profitLoss}
                  balanceSheet={balanceSheet}
                  trialTotals={trialTotals}
                  profitTotals={profitTotals}
                  balanceTotals={balanceTotals}
                />
              )
            }
          ]}
        />
      ) : pdfCards}

      <ReportPreviewDrawer pdfViewer={pdfViewer} onClose={closePdfViewer} />
    </div>
  );
}

function LedgerVouchersReport({ ready }: { ready: boolean }) {
  const tenant = useTenantStore();
  const [filters, setFilters] = useState(getDefaultLedgerFilters);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedLedger, setSelectedLedger] = useState<LedgerVoucherRow | null>(null);
  const [ledgerDrill, setLedgerDrill] = useState<LedgerVoucherRow | null>(null);
  const [previewBillId, setPreviewBillId] = useState<number | null>(null);
  const [previewBillRef, setPreviewBillRef] = useState("");
  const [openMonthKeys, setOpenMonthKeys] = useState<string[]>([]);
  const [transactionFilters, setTransactionFilters] = useState({ fromDate: "", toDate: "" });

  const ledgers = useQuery({
    queryKey: ["ledger-vouchers"],
    enabled: ready,
    queryFn: getLedgerVoucherSummary
  });

  const ledgerId = Number(ledgerDrill?.id ?? ledgerDrill?.ledgerId ?? ledgerDrill?.accountLedgerId ?? 0);
  const months = useQuery({
    queryKey: ["ledger-voucher-monthly", ledgerId],
    enabled: !!ledgerId,
    queryFn: () => getLedgerVoucherMonthly(ledgerId)
  });
  const filteredLedgers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (ledgers.data ?? []).filter((row) => {
      const category = getLedgerCategory(row);
      const matchesCategory = !categoryFilter || category === categoryFilter;
      const matchesSearch = !q ||
        getLedgerName(row).toLowerCase().includes(q) ||
        getAccountGroup(row).toLowerCase().includes(q) ||
        category.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [categoryFilter, ledgers.data, search]);

  const categoryOptions = useMemo(() => {
    const categories = Array.from(new Set((ledgers.data ?? []).map(getLedgerCategory))).sort((a, b) => a.localeCompare(b));
    return [{ value: "", label: "All Categories" }, ...categories.map((category) => ({ value: category, label: category }))];
  }, [ledgers.data]);

  const ledgerTotals = useMemo(() => {
    const debit = filteredLedgers.reduce((sum, row) => sum + ledgerDebit(row), 0);
    const credit = filteredLedgers.reduce((sum, row) => sum + ledgerCredit(row), 0);
    return { debit, credit, net: debit - credit };
  }, [filteredLedgers]);

  const ledgerColumns = useMemo<ColumnsType<LedgerVoucherRow>>(() => [
    {
      title: "Particulars",
      key: "ledger",
      width: 300,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{getLedgerName(row)}</Typography.Text>
          <Typography.Text type="secondary" className="ledger-table-hint">Double-click to open voucher details</Typography.Text>
        </Space>
      )
    },
    {
      title: "Account Group",
      dataIndex: "accountGroup",
      key: "accountGroup",
      width: 220,
      render: (_, row) => <Tag>{getAccountGroup(row)}</Tag>
    },
    {
      title: "Debit (Dr)",
      key: "debit",
      align: "right",
      width: 160,
      render: (_, row) => numberOrDash(ledgerDebit(row))
    },
    {
      title: "Credit (Cr)",
      key: "credit",
      align: "right",
      width: 160,
      render: (_, row) => numberOrDash(ledgerCredit(row))
    },
    {
      title: "Closing Balance",
      key: "closingBalance",
      align: "right",
      width: 180,
      render: (_, row) => (
        <span className={balanceClass(getBalanceType(row))}>
          {balanceText(row)}
        </span>
      )
    }
  ], []);

  const openLedger = (row: LedgerVoucherRow) => {
    setSelectedLedger(row);
    setLedgerDrill(row);
    setOpenMonthKeys([]);
    setTransactionFilters({ fromDate: "", toDate: "" });
  };

  return (
    <div className="ledger-report-shell">
      <PageHeaderActions title="Ledger Vouchers" subtitle={tenant.orgName ? `${tenant.orgName} ledger movement and voucher drill-down` : "Ledger movement and voucher drill-down"} />

      <Card className="ledger-sticky-panel">
        <div className="ledger-compact-metrics">
          <div className="ledger-compact-metric">
            <span>Total Debit</span>
            <strong>{formatInr(ledgerTotals.debit)}</strong>
          </div>
          <div className="ledger-compact-metric">
            <span>Total Credit</span>
            <strong>{formatInr(ledgerTotals.credit)}</strong>
          </div>
          <div className="ledger-compact-metric">
            <span>Net Balance</span>
            <strong className={balanceClass(ledgerTotals.net >= 0 ? "Dr" : "Cr")}>{formatInr(Math.abs(ledgerTotals.net))} {ledgerTotals.net >= 0 ? "Dr" : "Cr"}</strong>
          </div>
        </div>
        <div className="ledger-report-toolbar">
          <Space wrap size={10}>
            <DatePicker.RangePicker
              size="small"
              allowClear={false}
              value={dateRangeValue(filters.fromDate, filters.toDate)}
              onChange={(value) => setFilters({
                fromDate: value?.[0]?.format("YYYY-MM-DD") ?? filters.fromDate,
                toDate: value?.[1]?.format("YYYY-MM-DD") ?? filters.toDate
              })}
            />
            <Select
              size="small"
              className="ledger-category-select"
              value={categoryFilter}
              options={categoryOptions}
              onChange={setCategoryFilter}
            />
            <Input
              size="small"
              className="ledger-search-input"
              placeholder="Search ledger, group, category"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Button size="small" type="primary" icon={<ReloadOutlined />} onClick={() => ledgers.refetch()}>Refresh</Button>
          </Space>
          <Space wrap>
            <Tag color="blue">{formatPeriod(filters.fromDate, filters.toDate)}</Tag>
            <Tag>{filteredLedgers.length} ledger{filteredLedgers.length === 1 ? "" : "s"}</Tag>
          </Space>
        </div>
      </Card>

      <Card className="ledger-table-card" title="Ledgers List" extra={<Tag color={ledgerTotals.net >= 0 ? "green" : "red"}>{filteredLedgers.length} ledger{filteredLedgers.length === 1 ? "" : "s"}</Tag>}>
        {!ready ? (
          <Empty description="Select company and financial year to load ledgers." />
        ) : (
          <div className="lv-wrap">
            {ledgers.isLoading ? (
              <div className="lv-spin"><Spin size="small" /> Loading ledgers...</div>
            ) : (
              <div className="lv-main-scroll">
                <table className="lv-tbl">
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th>Account Group</th>
                      <th className="num">Debit (Dr)</th>
                      <th className="num">Credit (Cr)</th>
                      <th className="num">Closing Balance</th>
                    </tr>
                  </thead>
                  <tbody>{renderLedgerRows(filteredLedgers, selectedLedger, setSelectedLedger, openLedger)}</tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Card>

      <LedgerVoucherDrawer
        ledger={ledgerDrill}
        ledgerId={ledgerId}
        months={months.data ?? []}
        monthsLoading={months.isLoading}
        openMonthKeys={openMonthKeys}
        setOpenMonthKeys={setOpenMonthKeys}
        transactionFilters={transactionFilters}
        setTransactionFilters={setTransactionFilters}
        period={formatPeriod(filters.fromDate, filters.toDate)}
        onPreviewBill={(sourceId, voucherRef) => {
          setPreviewBillId(sourceId);
          setPreviewBillRef(voucherRef);
        }}
        onClose={() => { setLedgerDrill(null); setOpenMonthKeys([]); }}
      />
      <LedgerBillPreviewDrawer billId={previewBillId} voucherRef={previewBillRef} onClose={() => { setPreviewBillId(null); setPreviewBillRef(""); }} />
    </div>
  );
}

function FinancialStatementsPanel({
  trialBalance,
  profitLoss,
  balanceSheet,
  trialTotals,
  profitTotals,
  balanceTotals
}: {
  trialBalance: { data?: any[]; isLoading: boolean };
  profitLoss: { data?: any; isLoading: boolean };
  balanceSheet: { data?: any; isLoading: boolean };
  trialTotals: { debit: number; credit: number; balance: number };
  profitTotals: { income: number; expense: number; assets: number; liabilities: number };
  balanceTotals: { income: number; expense: number; assets: number; liabilities: number };
}) {
  return (
    <Space direction="vertical" size={12} className="report-full-width">
      <Row gutter={[12, 12]}>
        <Col xs={24} md={8}><MetricCard title="Trial Debit" value={trialTotals.debit} /></Col>
        <Col xs={24} md={8}><MetricCard title="Trial Credit" value={trialTotals.credit} /></Col>
        <Col xs={24} md={8}><MetricCard title="Net Profit" value={profitLoss.data?.netProfit ?? 0} /></Col>
      </Row>
      <Card
        title={<Space><BarChartOutlined /> Trial Balance</Space>}
        extra={<Tag color={Math.abs(trialTotals.debit - trialTotals.credit) < 0.01 ? "success" : "warning"}>Diff {formatInr(trialTotals.debit - trialTotals.credit)}</Tag>}
      >
        <PersistedTable
          tableId="reports-trial-balance"
          rowKey={(row, index) => `${row.ledger}-${index}`}
          size="small"
          columns={trialColumns}
          dataSource={trialBalance.data ?? []}
          loading={trialBalance.isLoading}
          pagination={{ pageSize: 100 }}
        />
      </Card>
      <Row gutter={[12, 12]}>
        <Col xs={24} xl={12}>
          <StatementCard
            title="Profit & Loss"
            loading={profitLoss.isLoading}
            leftTitle="Income"
            leftGroups={profitLoss.data?.income ?? []}
            leftTotal={profitTotals.income}
            rightTitle="Expense"
            rightGroups={profitLoss.data?.expense ?? []}
            rightTotal={profitTotals.expense}
            footerLabel={profitLoss.data?.netProfit && profitLoss.data.netProfit < 0 ? "Net Loss" : "Net Profit"}
            footerValue={profitLoss.data?.netProfit ?? 0}
          />
        </Col>
        <Col xs={24} xl={12}>
          <StatementCard
            title="Balance Sheet"
            loading={balanceSheet.isLoading}
            leftTitle="Assets"
            leftGroups={balanceSheet.data?.assets ?? []}
            leftTotal={balanceTotals.assets}
            rightTitle="Liabilities"
            rightGroups={balanceSheet.data?.liabilities ?? []}
            rightTotal={balanceTotals.liabilities}
            footerLabel="Current Year Profit"
            footerValue={balanceSheet.data?.netProfit ?? 0}
          />
        </Col>
      </Row>
    </Space>
  );
}

function ReportCardGrid({ loading, reports, onPreview }: { loading: boolean; reports: PdfReportType[]; onPreview: (reportType: string, title: string) => void }) {
  if (loading) return <div className="report-card-loader"><Spin size="small" /> Loading reports...</div>;
  if (!reports.length) return <div className="report-card-loader">No reports registered yet.</div>;

  return (
    <Row gutter={[12, 12]} className="report-card-grid">
      {reports.map((report) => (
        <Col xs={24} md={8} xl={6} key={report.reportType}>
          <Card className="report-centre-card" onClick={() => onPreview(report.reportType, report.title)}>
            <div className="report-centre-card-body">
              <FilePdfOutlined className="report-card-icon" />
              <div>
                <Typography.Text strong>{report.title}</Typography.Text>
                <Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }}>{report.description}</Typography.Paragraph>
              </div>
            </div>
            <Button size="small" block icon={<EyeOutlined />} onClick={(event) => { event.stopPropagation(); onPreview(report.reportType, report.title); }}>Preview</Button>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function LedgerVoucherDrawer({
  ledger,
  ledgerId,
  months,
  monthsLoading,
  openMonthKeys,
  setOpenMonthKeys,
  transactionFilters,
  setTransactionFilters,
  period,
  onPreviewBill,
  onClose
}: {
  ledger: LedgerVoucherRow | null;
  ledgerId: number;
  months: LedgerMonthRow[];
  monthsLoading: boolean;
  openMonthKeys: string[];
  setOpenMonthKeys: Dispatch<SetStateAction<string[]>>;
  transactionFilters: { fromDate: string; toDate: string };
  setTransactionFilters: (filters: { fromDate: string; toDate: string }) => void;
  period: string;
  onPreviewBill: (sourceId: number, voucherRef: string) => void;
  onClose: () => void;
}) {
  const hasTransactionRange = Boolean(transactionFilters.fromDate && transactionFilters.toDate);
  const monthTotals = useMemo(() => {
    const hasMonths = months.length > 0;
    const debit = hasMonths ? months.reduce((sum, row) => sum + monthDebit(row), 0) : ledgerDebit(ledger);
    const credit = hasMonths ? months.reduce((sum, row) => sum + monthCredit(row), 0) : ledgerCredit(ledger);
    return { debit, credit, net: debit - credit };
  }, [ledger, months]);
  const flatTransactions = useQuery({
    queryKey: ["ledger-voucher-transactions-flat", ledgerId],
    enabled: hasTransactionRange && !!ledgerId,
    queryFn: () => getLedgerVoucherTransactions(ledgerId)
  });
  const filteredTransactions = useMemo(
    () => filterTransactionsByDate(flatTransactions.data ?? [], transactionFilters.fromDate, transactionFilters.toDate),
    [flatTransactions.data, transactionFilters.fromDate, transactionFilters.toDate]
  );

  const toggleMonth = (month: LedgerMonthRow) => {
    const key = monthRowKey(month);
    setOpenMonthKeys((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  };

  return (
    <Drawer
      open={!!ledger}
      onClose={onClose}
      width="100vw"
      className="ledger-detail-drawer"
      title={(
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{ledger ? getLedgerName(ledger) : "Ledger Vouchers"}</Typography.Text>
          <Typography.Text type="secondary">{period}</Typography.Text>
        </Space>
      )}
      extra={<Tag>{ledger ? getAccountGroup(ledger) : "Ungrouped"}</Tag>}
    >
      {monthsLoading ? (
        <div className="ledger-drawer-loader"><Spin size="small" /> Loading month-wise register...</div>
      ) : (
        <div className="ledger-register-workspace">
          <div className="ledger-register-toolbar">
            <div className="ledger-compact-summary">
              <span><strong>Debit</strong> {formatInr(monthTotals.debit)}</span>
              <span><strong>Credit</strong> {formatInr(monthTotals.credit)}</span>
              <span className={balanceClass(monthTotals.net >= 0 ? "Dr" : "Cr")}><strong>Net</strong> {balanceText({ ...ledger, closingBalance: monthTotals.net, balanceType: monthTotals.net >= 0 ? "Dr" : "Cr" })}</span>
            </div>
            <DatePicker.RangePicker
              size="small"
              value={dateRangeValue(transactionFilters.fromDate, transactionFilters.toDate)}
              onChange={(value) => setTransactionFilters({
                fromDate: value?.[0]?.format("YYYY-MM-DD") ?? "",
                toDate: value?.[1]?.format("YYYY-MM-DD") ?? ""
              })}
            />
          </div>
          {hasTransactionRange ? (
            <div className="ledger-register-scroll ledger-flat-details">
              {flatTransactions.isLoading ? (
                <div className="lv-txn-loading"><Spin size="small" /> Loading date-wise transactions...</div>
              ) : (
                <DateWiseTransactionsTable rows={filteredTransactions} onPreviewBill={onPreviewBill} />
              )}
            </div>
          ) : (
            <div className="ledger-register-scroll">
              <table className="lv-reg-tbl ledger-register-table">
                <colgroup>
                  <col style={{ width: 170 }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "18%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={2}>Month</th>
                    <th colSpan={3} className="text-center">Transactions</th>
                    <th className="text-center num">Closing Balance</th>
                  </tr>
                  <tr className="lv-sub-head">
                    <th className="num">Debit</th>
                    <th className="num">Credit</th>
                    <th className="num">Net</th>
                    <th className="num">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {months.length ? months.flatMap((month) => renderMonthRows(month, ledgerId, openMonthKeys, toggleMonth, transactionFilters, onPreviewBill)) : (
                    <tr><td colSpan={5}><Empty description="No transactions for this ledger" /></td></tr>
                  )}
                  {months.length ? renderMonthGrandTotal(months) : null}
                  {months.length ? (
                    <tr className="ledger-register-chart-row">
                      <td colSpan={5}><MonthlyTrend months={months} /></td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function renderLedgerRows(rows: LedgerVoucherRow[], active: LedgerVoucherRow | null, selectLedger: (row: LedgerVoucherRow) => void, openLedger: (row: LedgerVoucherRow) => void) {
  if (!rows.length) return <tr><td colSpan={5} className="text-center">No ledgers found.</td></tr>;
  const groups = rows.reduce<Record<string, LedgerVoucherRow[]>>((acc, row) => {
    const group = getAccountGroup(row);
    acc[group] = [...(acc[group] ?? []), row];
    return acc;
  }, {});
  const output: JSX.Element[] = [];
  Object.entries(groups).forEach(([group, items]) => {
    output.push(<tr className="lv-grp" key={`group-${group}`}><td colSpan={5}>{group}</td></tr>);
    items.forEach((row) => {
      const id = Number(row.id ?? row.ledgerId ?? row.accountLedgerId ?? 0);
      const activeId = Number(active?.id ?? active?.ledgerId ?? active?.accountLedgerId ?? 0);
      output.push(
        <tr className={id === activeId ? "lv-month-active" : ""} key={`ledger-${id}`} onClick={() => selectLedger(row)} onDoubleClick={() => openLedger(row)}>
          <td>{getLedgerName(row)}</td>
          <td className="text-muted">{getAccountGroup(row)}</td>
          <td className="num">{numberOrDash(ledgerDebit(row))}</td>
          <td className="num">{numberOrDash(ledgerCredit(row))}</td>
          <td className={`num ${balanceClass(getBalanceType(row))}`}>{balanceText(row)}</td>
        </tr>
      );
    });
  });
  const debit = rows.reduce((sum, row) => sum + ledgerDebit(row), 0);
  const credit = rows.reduce((sum, row) => sum + ledgerCredit(row), 0);
  const net = debit - credit;
  output.push(
    <tr className="lv-total" key="grand-total">
      <td colSpan={2}>Grand Total</td>
      <td className="num">{formatInr(debit)}</td>
      <td className="num">{formatInr(credit)}</td>
      <td className={`num ${balanceClass(net >= 0 ? "Dr" : "Cr")}`}>{formatInr(Math.abs(net))} <small>{net >= 0 ? "Dr" : "Cr"}</small></td>
    </tr>
  );
  return output;
}

function renderMonthRows(
  month: LedgerMonthRow,
  ledgerId: number,
  openMonthKeys: string[],
  toggleMonth: (row: LedgerMonthRow) => void,
  transactionFilters: { fromDate: string; toDate: string },
  onPreviewBill: (sourceId: number, voucherRef: string) => void
) {
  const key = monthRowKey(month);
  const isOpen = openMonthKeys.includes(key);
  const debit = monthDebit(month);
  const credit = monthCredit(month);
  const net = debit - credit;
  return [
    <tr key={`month-${key}`} className={isOpen ? "lv-month-row lv-month-active" : "lv-month-row"} onClick={() => toggleMonth(month)}>
      <td><span className="lv-expand-icon">{isOpen ? <DownOutlined /> : <RightOutlined />}</span>{getMonthName(month) ?? key}</td>
      <td className="num">{numberOrDash(debit)}</td>
      <td className="num">{numberOrDash(credit)}</td>
      <td className={`num ${balanceClass(net >= 0 ? "Dr" : "Cr")}`}>{formatInr(Math.abs(net))} <small>{net >= 0 ? "Dr" : "Cr"}</small></td>
      <td className={`num ${balanceClass(getBalanceType(month))}`}>{formatInr(monthClosing(month))} <small>{getBalanceType(month)}</small></td>
    </tr>,
    isOpen ? (
      <tr key={`txn-${key}`} className="lv-txn-expand open">
        <td colSpan={5}>
          <div className="lv-txn-inner">
            <MonthTransactions
              ledgerId={ledgerId}
              month={month}
              monthName={getMonthName(month) ?? key}
              transactionFilters={transactionFilters}
              onPreviewBill={onPreviewBill}
            />
          </div>
        </td>
      </tr>
    ) : null
  ].filter(Boolean);
}

function MonthTransactions({
  ledgerId,
  month,
  monthName,
  transactionFilters,
  onPreviewBill
}: {
  ledgerId: number;
  month: LedgerMonthRow;
  monthName: string;
  transactionFilters: { fromDate: string; toDate: string };
  onPreviewBill: (sourceId: number, voucherRef: string) => void;
}) {
  const transactions = useQuery({
    queryKey: ["ledger-voucher-transactions", ledgerId, month.year, month.month, transactionFilters.fromDate, transactionFilters.toDate],
    enabled: !!ledgerId,
    queryFn: () => getLedgerVoucherTransactions(ledgerId, { year: month.year, month: month.month, fromDate: transactionFilters.fromDate, toDate: transactionFilters.toDate })
  });
  const rows = useMemo(
    () => filterTransactionsByDate(transactions.data ?? [], transactionFilters.fromDate, transactionFilters.toDate),
    [transactions.data, transactionFilters.fromDate, transactionFilters.toDate]
  );

  return transactions.isLoading ? <div className="lv-txn-loading"><Spin size="small" /> Loading...</div> : <TransactionsTable rows={rows} monthName={monthName} onPreviewBill={onPreviewBill} />;
}

function TransactionsTable({ rows, monthName, onPreviewBill }: { rows: LedgerTransactionRow[]; monthName: string; onPreviewBill: (sourceId: number, voucherRef: string) => void }) {
  if (!rows.length) return <div className="lv-txn-loading">No transactions in {monthName}.</div>;
  const debit = rows.reduce((sum, row) => sum + transactionDebit(row), 0);
  const credit = rows.reduce((sum, row) => sum + transactionCredit(row), 0);
  const last = rows[rows.length - 1];
  return (
    <table className="lv-transactions-table">
      <colgroup>
        <col style={{ width: 112 }} />
        <col />
        <col style={{ width: 132 }} />
        <col style={{ width: 126 }} />
        <col style={{ width: 126 }} />
        <col style={{ width: 126 }} />
        <col style={{ width: 148 }} />
      </colgroup>
      <thead>
        <tr><th>Date</th><th>Particulars</th><th>Vch Type</th><th className="num">Vch Ref</th><th className="num">Debit</th><th className="num">Credit</th><th className="num">Balance</th></tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr
            key={`${getTransactionDate(row)}-${getTransactionSourceModule(row)}-${getTransactionSourceId(row)}-${index}`}
            style={getTransactionSourceModule(row) === "BillEntry" && Number(getTransactionSourceId(row)) > 0 ? { cursor: "pointer" } : undefined}
            onClick={() => {
              if (getTransactionSourceModule(row) === "BillEntry" && Number(getTransactionSourceId(row)) > 0) {
                onPreviewBill(Number(getTransactionSourceId(row)), getTransactionVoucherRef(row));
              }
            }}
          >
            <td>{formatDate(getTransactionDate(row))}</td>
            <td>{getTransactionParticulars(row)}</td>
            <td><Tag className="lv-voucher-type-tag">{getTransactionVoucherType(row)}</Tag></td>
            <td className="num text-primary">{getTransactionVoucherRef(row)}</td>
            <td className="num">{numberOrDash(transactionDebit(row))}</td>
            <td className="num">{numberOrDash(transactionCredit(row))}</td>
            <td className={`num ${balanceClass(getBalanceType(row))}`}>{formatInr(transactionRunningBalance(row))} <small>{getBalanceType(row)}</small></td>
          </tr>
        ))}
        <tr className="lv-txn-tot"><td colSpan={4}>Closing</td><td className="num">{formatInr(debit)}</td><td className="num">{formatInr(credit)}</td><td className={`num ${balanceClass(getBalanceType(last))}`}>{formatInr(transactionRunningBalance(last))} <small>{getBalanceType(last)}</small></td></tr>
      </tbody>
    </table>
  );
}

function DateWiseTransactionsTable({ rows, onPreviewBill }: { rows: LedgerTransactionRow[]; onPreviewBill: (sourceId: number, voucherRef: string) => void }) {
  if (!rows.length) return <div className="lv-txn-loading">No transactions found for the selected date range.</div>;
  const orderedRows = [...rows].sort((left, right) => {
    const leftTime = new Date(getTransactionDate(left)).getTime();
    const rightTime = new Date(getTransactionDate(right)).getTime();
    if (leftTime !== rightTime) return leftTime - rightTime;
    return getTransactionVoucherRef(left).localeCompare(getTransactionVoucherRef(right));
  });
  return (
    <div className="lv-flat-details-inner">
      <div className="lv-flat-details-header">Date-wise transaction register</div>
      <div className="lv-flat-details-table-wrap">
        <TransactionsTable rows={orderedRows} monthName="selected period" onPreviewBill={onPreviewBill} />
      </div>
    </div>
  );
}

function LedgerBillPreviewDrawer({ billId, voucherRef, onClose }: { billId: number | null; voucherRef: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const canUpdate = hasPermission("BillEntry.Update");
  const [editOpen, setEditOpen] = useState(false);
  const query = useQuery({
    queryKey: ["ledger-voucher-preview-bill", billId],
    enabled: !!billId,
    queryFn: () => getBillEntry<any>(billId!)
  });

  return (
    <Drawer
      open={!!billId}
      onClose={onClose}
      width={960}
      title={voucherRef ? `Bill Entry - ${voucherRef}` : "Bill Entry"}
      extra={canUpdate && query.data?.id ? (
        <Button size="small" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
          Edit
        </Button>
      ) : null}
    >
      {query.isLoading ? (
        <div className="lv-spin"><Spin size="small" /> Loading entry details...</div>
      ) : !query.data ? (
        <Empty description="Unable to load entry details." />
      ) : (
        <>
          <BillPreviewContent bill={query.data} />
          {canUpdate && isVoucherKind(query.data.voucherType) ? (
            <VoucherEntryDrawer
              open={editOpen}
              onClose={() => setEditOpen(false)}
              voucherType={query.data.voucherType}
              title={query.data.voucherType}
              editId={query.data.id}
              onSaved={() => {
                setEditOpen(false);
                query.refetch();
                queryClient.invalidateQueries({ queryKey: ["ledger-vouchers"] });
                queryClient.invalidateQueries({ queryKey: ["ledger-voucher-monthly"] });
                queryClient.invalidateQueries({ queryKey: ["ledger-voucher-transactions"] });
                queryClient.invalidateQueries({ queryKey: ["ledger-voucher-transactions-flat"] });
              }}
            />
          ) : null}
        </>
      )}
    </Drawer>
  );
}

function BillPreviewContent({ bill }: { bill: any }) {
  const lineItems = Array.isArray(bill.lineItems) ? bill.lineItems : [];
  const hasBankDetails = Boolean(bill.bankAccountId || bill.bankName || bill.bankAccountNumber || bill.BankAccountId || bill.BankName || bill.BankAccountNumber);
  const bankBalance = {
    closingBalance: bill.bankClosingBalance ?? bill.BankClosingBalance,
    balanceType: bill.bankBalanceType ?? bill.BankBalanceType,
    facilityType: bill.bankFacilityType ?? bill.BankFacilityType,
    overdraftLimit: bill.bankOverdraftLimit ?? bill.BankOverdraftLimit,
    drawingPower: bill.bankDrawingPower ?? bill.BankDrawingPower,
    allowOverLimit: bill.bankAllowOverLimit ?? bill.BankAllowOverLimit,
    overLimitTolerance: bill.bankOverLimitTolerance ?? bill.BankOverLimitTolerance,
    availableOverdraftLimit: bill.bankAvailableOverdraftLimit ?? bill.BankAvailableOverdraftLimit
  };
  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <table className="lv-tbl" style={{ tableLayout: "auto" }}>
            <tbody>
              <tr><td>Voucher Type</td><td><Tag>{bill.voucherType ?? "-"}</Tag></td></tr>
              <tr><td>Bill No</td><td><strong>{bill.billNo ?? "-"}</strong></td></tr>
              <tr><td>Bill Date</td><td>{formatDate(bill.billDate)}</td></tr>
              {bill.dueDate ? <tr><td>Due Date</td><td>{formatDate(bill.dueDate)}</td></tr> : null}
              {bill.referenceNo ? <tr><td>Ref No</td><td>{bill.referenceNo}</td></tr> : null}
            </tbody>
          </table>
        </Col>
        <Col xs={24} md={12}>
          <table className="lv-tbl" style={{ tableLayout: "auto" }}>
            <tbody>
              <tr><td>Party</td><td><strong>{bill.partyName ?? bill.partyLabel ?? "-"}</strong></td></tr>
              <tr><td>Ledger</td><td>{bill.ledgerName ?? "-"}</td></tr>
              {hasBankDetails ? <tr><td>Bank</td><td>{bill.bankName ?? bill.BankName ?? "-"}</td></tr> : null}
              {hasBankDetails ? <tr><td>Bank A/C</td><td>{bill.bankAccountNumber ?? bill.BankAccountNumber ?? bill.bankAccountName ?? bill.BankAccountName ?? "-"}</td></tr> : null}
              {hasBankDetails ? <tr><td>Bank Balance</td><td>{balanceText(bankBalance)}</td></tr> : null}
              <tr><td>Posted</td><td><Tag color={bill.isPosted ? "green" : "orange"}>{bill.isPosted ? "Yes" : "No"}</Tag></td></tr>
              <tr><td>Entered By</td><td>{bill.enteredBy ?? "-"}</td></tr>
            </tbody>
          </table>
        </Col>
      </Row>
      {lineItems.length ? (
        <div className="lv-main-scroll" style={{ maxHeight: 320, marginBottom: 16 }}>
          <table className="lv-tbl">
            <thead>
              <tr>
                <th>Item / Description</th>
                <th className="num">Qty</th>
                <th className="num">Rate</th>
                <th className="num">Amount</th>
                <th className="num">GST %</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((line: any, index: number) => (
                <tr key={`${line.ledgerName ?? "line"}-${index}`}>
                  <td>{line.ledgerName ?? "-"}</td>
                  <td className="num">{line.quantity ?? "-"}</td>
                  <td className="num">{line.rate != null ? formatInr(line.rate) : "-"}</td>
                  <td className="num">{line.amount != null ? formatInr(line.amount) : "-"}</td>
                  <td className="num">{line.gstPercent > 0 ? `${line.gstPercent}%` : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <table className="lv-tbl" style={{ width: "auto", tableLayout: "auto" }}>
          <tbody>
            {Number(bill.taxableAmount ?? 0) > 0 ? <tr><td>Taxable Amount</td><td className="num">{formatInr(bill.taxableAmount)}</td></tr> : null}
            {Number(bill.cgstTotal ?? 0) > 0 ? <tr><td>CGST</td><td className="num">{formatInr(bill.cgstTotal)}</td></tr> : null}
            {Number(bill.sgstTotal ?? 0) > 0 ? <tr><td>SGST</td><td className="num">{formatInr(bill.sgstTotal)}</td></tr> : null}
            {Number(bill.igstTotal ?? 0) > 0 ? <tr><td>IGST</td><td className="num">{formatInr(bill.igstTotal)}</td></tr> : null}
            {Number(bill.tdsTotal ?? 0) > 0 ? <tr><td>TDS</td><td className="num">- {formatInr(bill.tdsTotal)}</td></tr> : null}
            <tr className="lv-total"><td>Grand Total</td><td className="num">{formatInr(bill.grandTotal ?? bill.totalAmount ?? 0)}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function renderMonthGrandTotal(months: LedgerMonthRow[]) {
  const debit = months.reduce((sum, row) => sum + monthDebit(row), 0);
  const credit = months.reduce((sum, row) => sum + monthCredit(row), 0);
  const net = debit - credit;
  const last = months[months.length - 1];
  return (
    <tr className="lv-grand-row">
      <td>Grand Total</td>
      <td className="num">{formatInr(debit)}</td>
      <td className="num">{formatInr(credit)}</td>
      <td className={`num ${balanceClass(net >= 0 ? "Dr" : "Cr")}`}>{formatInr(Math.abs(net))} <small>{net >= 0 ? "Dr" : "Cr"}</small></td>
      <td className={`num ${balanceClass(getBalanceType(last))}`}>{formatInr(monthClosing(last))} <small>{getBalanceType(last)}</small></td>
    </tr>
  );
}

function MonthlyTrend({ months }: { months: LedgerMonthRow[] }) {
  const max = Math.max(...months.flatMap((month) => [monthDebit(month), monthCredit(month)]), 1);
  return (
    <div className="lv-chart-wrap">
      <div className="lv-chart-title">Monthly Dr/Cr Trend</div>
      <div className="lv-chart-bars">
        {months.map((month) => {
          const debit = monthDebit(month);
          const credit = monthCredit(month);
          return (
            <div className="lv-chart-month" key={`${month.year}-${month.month}`}>
              <div className="lv-chart-values">
                <span className="dr">{compactInr(debit)}</span>
                <span className="cr">{compactInr(credit)}</span>
              </div>
              <div className="lv-chart-pair">
                <span className="dr" style={{ height: `${Math.max(4, (debit / max) * 100)}%` }} />
                <span className="cr" style={{ height: `${Math.max(4, (credit / max) * 100)}%` }} />
              </div>
              <small>{String(month.monthName ?? month.month).split(" ")[0]}</small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getDefaultReportFilters() {
  return { fromDate: "", toDate: "", search: "" };
}

function getDefaultLedgerFilters() {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return { fromDate: `${year}-04-01`, toDate: now.toISOString().slice(0, 10) };
}

function buildPdfPayload(filters: { fromDate?: string; toDate?: string; search?: string }) {
  return { fromDate: filters.fromDate || undefined, toDate: filters.toDate || undefined, search: filters.search || undefined, params: {} };
}

function formatPeriod(from?: string, to?: string) {
  return from && to ? `${formatDate(from)} to ${formatDate(to)}` : "";
}

function dateRangeValue(fromDate?: string, toDate?: string) {
  return fromDate && toDate ? [dayjs(fromDate), dayjs(toDate)] as [dayjs.Dayjs, dayjs.Dayjs] : null;
}

function ledgerRowKey(row?: LedgerVoucherRow | null) {
  if (!row) return "";
  return String(row.id ?? row.ledgerId ?? row.accountLedgerId ?? getLedgerName(row));
}

function monthRowKey(row?: LedgerMonthRow | null) {
  if (!row) return "";
  return `${row.year ?? ""}-${row.month ?? ""}-${row.monthName ?? ""}`;
}

function getLedgerName(row: LedgerVoucherRow) {
  const extended = row as LedgerVoucherRow & {
    Name?: string;
    LedgerName?: string;
  };
  return row.name
    ?? extended.Name
    ?? row.ledgerName
    ?? extended.LedgerName
    ?? `Ledger ${row.id ?? row.ledgerId ?? row.accountLedgerId ?? ""}`.trim();
}

function getLedgerCategory(row: LedgerVoucherRow) {
  const extended = row as LedgerVoucherRow & {
    mainCategory?: string;
    MainCategory?: string;
    category?: string;
    Category?: string;
    accountCategory?: string;
    AccountCategory?: string;
    accountType?: string;
    AccountType?: string;
    AccountGroup?: string;
  };
  return extended.mainCategory
    ?? extended.MainCategory
    ?? extended.category
    ?? extended.Category
    ?? extended.accountCategory
    ?? extended.AccountCategory
    ?? extended.accountType
    ?? extended.AccountType
    ?? row.accountGroup
    ?? extended.AccountGroup
    ?? "Ungrouped";
}

function getAccountGroup(row?: LedgerVoucherRow | null) {
  if (!row) return "Ungrouped";
  const extended = row as LedgerVoucherRow & { AccountGroup?: string };
  return row.accountGroup ?? extended.AccountGroup ?? "Ungrouped";
}

function pickAmount(source: Record<string, unknown> | null | undefined, keys: string[]) {
  if (!source) return 0;
  for (const key of keys) {
    const amount = Number(source[key] ?? Number.NaN);
    if (!Number.isNaN(amount)) return amount;
  }
  return 0;
}

function ledgerDebit(row?: LedgerVoucherRow | null) {
  return pickAmount(row as Record<string, unknown>, ["debit", "Debit", "debitTotal", "DebitTotal", "totalDebit", "TotalDebit", "debitAmount", "DebitAmount", "dr", "Dr", "drAmount", "DrAmount"]);
}

function ledgerCredit(row?: LedgerVoucherRow | null) {
  return pickAmount(row as Record<string, unknown>, ["credit", "Credit", "creditTotal", "CreditTotal", "totalCredit", "TotalCredit", "creditAmount", "CreditAmount", "cr", "Cr", "crAmount", "CrAmount"]);
}

function ledgerClosing(row?: LedgerVoucherRow | null) {
  return pickAmount(row as Record<string, unknown>, ["closingBalance", "ClosingBalance", "balance", "Balance", "closing", "Closing", "closingAmount", "ClosingAmount", "netBalance", "NetBalance"]);
}

function monthDebit(row?: LedgerMonthRow | null) {
  return pickAmount(row as Record<string, unknown>, ["debit", "Debit", "debitTotal", "DebitTotal", "totalDebit", "TotalDebit", "debitAmount", "DebitAmount", "dr", "Dr", "drAmount", "DrAmount"]);
}

function monthCredit(row?: LedgerMonthRow | null) {
  return pickAmount(row as Record<string, unknown>, ["credit", "Credit", "creditTotal", "CreditTotal", "totalCredit", "TotalCredit", "creditAmount", "CreditAmount", "cr", "Cr", "crAmount", "CrAmount"]);
}

function monthClosing(row?: LedgerMonthRow | null) {
  return pickAmount(row as Record<string, unknown>, ["closingBalance", "ClosingBalance", "balance", "Balance", "closing", "Closing", "closingAmount", "ClosingAmount", "netBalance", "NetBalance"]);
}

function transactionDebit(row?: LedgerTransactionRow | null) {
  return pickAmount(row as Record<string, unknown>, ["debit", "Debit", "debitAmount", "DebitAmount", "dr", "Dr", "drAmount", "DrAmount"]);
}

function transactionCredit(row?: LedgerTransactionRow | null) {
  return pickAmount(row as Record<string, unknown>, ["credit", "Credit", "creditAmount", "CreditAmount", "cr", "Cr", "crAmount", "CrAmount"]);
}

function transactionRunningBalance(row?: LedgerTransactionRow | null) {
  return pickAmount(row as Record<string, unknown>, ["runningBalance", "RunningBalance", "balance", "Balance", "closingBalance", "ClosingBalance", "netBalance", "NetBalance"]);
}

function getBalanceType(row?: Record<string, unknown> | null) {
  if (!row) return "";
  return String(row.balanceType ?? row.BalanceType ?? row.type ?? row.Type ?? "");
}

function getMonthName(row?: LedgerMonthRow | null) {
  if (!row) return "";
  return String((row as LedgerMonthRow & { MonthName?: string }).monthName ?? (row as LedgerMonthRow & { MonthName?: string }).MonthName ?? "");
}

function getTransactionDate(row?: LedgerTransactionRow | null) {
  if (!row) return "";
  return String((row as LedgerTransactionRow & { EntryDate?: string; Date?: string }).entryDate ?? (row as LedgerTransactionRow & { EntryDate?: string; Date?: string }).EntryDate ?? row.date ?? (row as LedgerTransactionRow & { Date?: string }).Date ?? "");
}

function getTransactionParticulars(row?: LedgerTransactionRow | null) {
  if (!row) return "-";
  const extended = row as LedgerTransactionRow & { Particulars?: string; Description?: string; Narration?: string };
  return row.particulars ?? extended.Particulars ?? row.description ?? extended.Description ?? row.narration ?? extended.Narration ?? "-";
}

function getTransactionVoucherType(row?: LedgerTransactionRow | null) {
  if (!row) return "-";
  const extended = row as LedgerTransactionRow & { VoucherType?: string; SourceModule?: string };
  return row.voucherType ?? extended.VoucherType ?? row.sourceModule ?? extended.SourceModule ?? "-";
}

function getTransactionVoucherRef(row?: LedgerTransactionRow | null) {
  if (!row) return "-";
  return String((row as LedgerTransactionRow & { VoucherRef?: string }).voucherRef ?? (row as LedgerTransactionRow & { VoucherRef?: string }).VoucherRef ?? "-");
}

function getTransactionSourceModule(row?: LedgerTransactionRow | null) {
  if (!row) return "";
  return String((row as LedgerTransactionRow & { SourceModule?: string }).sourceModule ?? (row as LedgerTransactionRow & { SourceModule?: string }).SourceModule ?? "");
}

function getTransactionSourceId(row?: LedgerTransactionRow | null) {
  if (!row) return "";
  return String((row as LedgerTransactionRow & { SourceId?: number }).sourceId ?? (row as LedgerTransactionRow & { SourceId?: number }).SourceId ?? "");
}

function numberOrDash(value?: number) {
  const amount = Number(value ?? 0);
  return amount > 0 ? formatInr(amount) : "-";
}

function compactInr(value?: number) {
  return new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 }).format(Number(value ?? 0));
}

function balanceClass(type?: string) {
  return String(type ?? "").toLowerCase() === "cr" ? "lv-cr" : "lv-dr";
}

function filterTransactionsByDate(rows: LedgerTransactionRow[], fromDate?: string, toDate?: string) {
  if (!fromDate && !toDate) return rows;
  const from = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
  const to = toDate ? new Date(`${toDate}T23:59:59`).getTime() : Number.POSITIVE_INFINITY;
  return rows.filter((row) => {
    const raw = getTransactionDate(row);
    if (!raw) return true;
    const time = new Date(raw).getTime();
    return Number.isFinite(time) ? time >= from && time <= to : true;
  });
}

function isVoucherKind(value: string): value is "SalesInvoice" | "PurchaseInvoice" | "ReceiptVoucher" | "PaymentVoucher" | "JournalEntry" {
  return value === "SalesInvoice"
    || value === "PurchaseInvoice"
    || value === "ReceiptVoucher"
    || value === "PaymentVoucher"
    || value === "JournalEntry";
}
